import { getLessonStorageKey } from './lessonRegistry';

const isStorageAvailable = () => (
  typeof window !== 'undefined' &&
  Boolean(window.localStorage)
);

const normalizeSimulationCode = (value) => (
  String(value || '').replace(/\r\n?/g, '\n')
);

const normalizeProgress = (lesson, value) => {
  if (!lesson || !value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  if (value.lessonId !== lesson.id || Number(value.lessonVersion) !== Number(lesson.version)) {
    return null;
  }

  if (!value.projectState || !Array.isArray(value.projectState.components)) {
    return null;
  }

  return {
    lessonId: lesson.id,
    lessonVersion: lesson.version,
    currentStepId: String(value.currentStepId || lesson.steps?.[0]?.id || ''),
    projectState: {
      components: Array.isArray(value.projectState.components) ? value.projectState.components : [],
      connections: Array.isArray(value.projectState.connections) ? value.projectState.connections : [],
      canvas: value.projectState.canvas && typeof value.projectState.canvas === 'object'
        ? value.projectState.canvas
        : {},
      simulation: value.projectState.simulation && typeof value.projectState.simulation === 'object'
        ? {
            code: normalizeSimulationCode(value.projectState.simulation.code),
          }
        : {},
    },
    starterSignature: String(value.starterSignature || ''),
    completedStepIds: Array.isArray(value.completedStepIds) ? value.completedStepIds : [],
    hintUsageByStepId: value.hintUsageByStepId && typeof value.hintUsageByStepId === 'object'
      ? value.hintUsageByStepId
      : {},
    selections: value.selections && typeof value.selections === 'object'
      ? value.selections
      : {},
    inspectedTargets: Array.isArray(value.inspectedTargets) ? value.inspectedTargets : [],
    completed: Boolean(value.completed),
    startedAt: Number(value.startedAt) || Date.now(),
    lastUpdatedAt: String(value.lastUpdatedAt || new Date().toISOString()),
  };
};

export const loadLessonProgress = (lesson) => {
  if (!isStorageAvailable()) {
    return null;
  }

  const storageKey = getLessonStorageKey(lesson);

  if (!storageKey) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(storageKey);

    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    const normalized = normalizeProgress(lesson, parsed);

    if (!normalized) {
      window.localStorage.removeItem(storageKey);
    }

    return normalized;
  } catch {
    window.localStorage.removeItem(storageKey);
    return null;
  }
};

export const saveLessonProgress = (lesson, progress) => {
  if (!isStorageAvailable()) {
    return;
  }

  const storageKey = getLessonStorageKey(lesson);

  if (!storageKey) {
    return;
  }

  const payload = normalizeProgress(lesson, {
    ...progress,
    lessonId: lesson.id,
    lessonVersion: lesson.version,
    lastUpdatedAt: new Date().toISOString(),
  });

  if (!payload) {
    return;
  }

  window.localStorage.setItem(storageKey, JSON.stringify(payload));
};

export const clearLessonProgress = (lesson) => {
  if (!isStorageAvailable()) {
    return;
  }

  const storageKey = getLessonStorageKey(lesson);

  if (storageKey) {
    window.localStorage.removeItem(storageKey);
  }
};

